import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

import { NameOrSkeleton } from './NameOrSkeleton';
import type { ResolvedMemberName } from './utils';

interface PrimarySectionProps {
  readonly label: string;
  readonly primary: ResolvedMemberName | null;
  readonly emptyLabel: string;
  readonly onClear: (() => void) | null;
}

/** Primary-clinician slot in the care-team editor, structurally distinct from
 * the supporting team list per PRD. */
export function PrimarySection({
  label,
  primary,
  emptyLabel,
  onClear,
}: PrimarySectionProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      <span className="block text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
        {label}
      </span>
      <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
        <span className="flex-1 text-[13px] text-foreground truncate">
          {primary === null ? (
            <span className="italic text-muted-foreground">{emptyLabel}</span>
          ) : (
            <NameOrSkeleton resolved={primary} />
          )}
        </span>
        {onClear !== null && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="h-7 text-[11px]"
          >
            {t('patient.careTeam.clearPrimary')}
          </Button>
        )}
      </div>
    </div>
  );
}
